import { useEffect, useRef, useState } from 'react';
import type { BotDifficulty, Ruleset } from '@xiangqi/protocol';
import { MODE_INFO } from '@xiangqi/protocol';
import { api, ensureSession } from '../api.ts';

const MODES: Ruleset[] = ['standard', 'blind', 'blind_si_chua', 'blind_tuong_vuong'];
const LEVELS: BotDifficulty[] = ['easy', 'medium', 'hard', 'expert'];

interface DevPanelProps {
  navigate(path: string): void;
}

/** Dev/admin-only: pair two bots and spectate the match live. */
export function DevPanel({ navigate }: DevPanelProps) {
  const [token, setToken] = useState(() => sessionStorage.getItem('xq_dev_token') ?? '');
  const [ruleset, setRuleset] = useState<Ruleset>('standard');
  const [red, setRed] = useState<BotDifficulty>('medium');
  const [black, setBlack] = useState<BotDifficulty>('medium');
  const [status, setStatus] = useState<string | null>(null);
  const polling = useRef(false);

  useEffect(() => {
    sessionStorage.setItem('xq_dev_token', token);
  }, [token]);

  const start = async (): Promise<void> => {
    if (polling.current) return;
    polling.current = true;
    setStatus('Đang xếp cặp bot…');
    try {
      const res = await fetch('/v1/dev/bot-match', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-dev-token': token },
        body: JSON.stringify({ ruleset, red_profile: red, black_profile: black }),
      });
      if (!res.ok) {
        setStatus(res.status === 401 ? 'Sai dev token' : `Lỗi ${res.status} (panel chưa bật?)`);
        return;
      }
      const { pair_id } = (await res.json()) as { pair_id: string };
      // Poll for the room the BotMaster reports back, then for the match id.
      for (let i = 0; i < 30; i += 1) {
        await new Promise((r) => setTimeout(r, 1_000));
        const poll = await fetch(`/v1/dev/bot-match/${pair_id}`, {
          headers: { 'x-dev-token': token },
        });
        const { room_code } = (await poll.json()) as { room_code: string | null };
        if (!room_code) continue;
        setStatus(`Bot đã tạo phòng ${room_code} — chờ vào trận…`);
        await ensureSession();
        const room = await api.room(room_code);
        if (room.room.match_id) {
          navigate(`/m/${room.room.match_id}`);
          return;
        }
      }
      setStatus('Hết thời gian chờ — BotMaster có đang chạy không?');
    } finally {
      polling.current = false;
    }
  };

  return (
    <main className="page">
      <h1>Dev · Bot vs Bot</h1>
      <section className="card">
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder="Dev token"
          aria-label="Dev token"
        />
        <div className="ruleset-picker">
          {MODES.map((m) => (
            <label key={m} className={ruleset === m ? 'on' : ''}>
              <input type="radio" name="dev-mode" checked={ruleset === m} onChange={() => setRuleset(m)} />
              {MODE_INFO[m].label}
            </label>
          ))}
        </div>
        <div className="ruleset-picker">
          {LEVELS.map((l) => (
            <label key={`r${l}`} className={red === l ? 'on' : ''}>
              <input type="radio" name="dev-red" checked={red === l} onChange={() => setRed(l)} />
              Đỏ: {l}
            </label>
          ))}
        </div>
        <div className="ruleset-picker">
          {LEVELS.map((l) => (
            <label key={`b${l}`} className={black === l ? 'on' : ''}>
              <input type="radio" name="dev-black" checked={black === l} onChange={() => setBlack(l)} />
              Đen: {l}
            </label>
          ))}
        </div>
        <button className="primary" onClick={() => void start()} disabled={!token}>
          Tạo trận Bot vs Bot & xem
        </button>
        {status && <p className="hint">{status}</p>}
      </section>
      <button className="link" onClick={() => navigate('/')}>
        ← Về trang chủ
      </button>
    </main>
  );
}
